<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Draggable from 'vuedraggable'
import { SortableEvent } from 'sortablejs'

import { EventType, TourEvent } from '@/types/delivery-management'
import { DragChangeEvent } from '@/types/sortable'
import { localeTime } from '@/utils/dates'
import { tourEventTypes } from '@/utils/delivery-management'

import DraggableShipmentCard from '@/components/delivery-management/tour-planner/DraggableShipmentCard.vue'

const { t } = useI18n()

interface EventGroup {
    id: string
    pickups: number
    deliveries: number
    address: string
    zipcodeCity: string
    tourEvents: TourEvent[]
}

interface Props {
    group: EventGroup
    index: number
}

const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'drag-change', value: DragChangeEvent<TourEvent>): void }>()

const expanded = ref(false)

const estimatedTime = computed(() => {
    const firstEvent = props.group.tourEvents[0]
    if (!firstEvent?.estimatedAt) return '-'
    return localeTime(firstEvent.estimatedAt)
})

function onShipmentMoved(event: SortableEvent): boolean {
    // We'll only allow moving to our tours
    return event.from !== event.to
}
</script>
<template>
    <div class="relative bg-primary-50 dark:bg-dark-500 rounded-xl flex-shrink-0">
        <div
            class="flex items-center py-2 px-2 w-full"
            :class="{ 'grouped-event': props.group.tourEvents.length > 1 && !expanded }"
        >
            <div class="flex flex-col items-center space-y-2 mr-2">
                <span
                    class="flex justify-center items-center p-0.5 aspect-square rounded-full text-xs font-mono bg-green-800 text-white"
                >
                    {{ props.index + 1 }}
                </span>

                <mdi:drag
                    class="handle cursor-grab text-xl text-dark-700 transition dark:text-white"
                />
            </div>

            <div class="flex flex-col mr-2 max-w-48">
                <span
                    v-truncate-tooltip="props.group.address"
                    class="text-primary-400 font-semibold text-sm truncate"
                >
                    {{ props.group.address }}
                </span>
                <span class="text-primary-400 font-semibold text-xs">
                    {{ props.group.zipcodeCity }}
                </span>
                <div class="flex justify-between text-xs">
                    <span class="font-mono">
                        D{{ props.group.deliveries }}P{{ props.group.pickups }}
                    </span>
                    <span>{{ estimatedTime }}</span>
                </div>
            </div>

            <mdi:chevron-down
                class="transition duration-300 cursor-pointer !ml-auto"
                :class="{ 'rotate-180': expanded }"
                @click="expanded = !expanded"
            />
        </div>

        <Draggable
            v-if="expanded"
            :list="props.group.tourEvents"
            item-key="id"
            handle=".handle"
            ghost-class="drag-ghost-card"
            :group="{ name: 'event-groups', put: 'clone', pull: true }"
            :move="onShipmentMoved"
            :animation="200"
            class="flex space-x-2 p-2"
            @change="emit('drag-change', $event)"
        >
            <template #item="{ element: event }">
                <DraggableShipmentCard v-if="event.shipment" :shipment="event.shipment" />
                <div v-else>{{ t(tourEventTypes[event.type as EventType]) }}</div>
            </template>
        </Draggable>
    </div>
</template>
